import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useNavigationBlock } from '../context/NavigationBlockContext';
import { UserRoles, RoleDisplayNames } from '../lib/utils';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  LayoutDashboard, 
  Users, 
  BookOpen, 
  Calendar, 
  Building2, 
  Award, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  ChevronDown,
  User,
  GraduationCap,
  Building,
  Shield
} from 'lucide-react';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { safeNavigate, isBlocked } = useNavigationBlock();
  
  const navItems = [
    { 
      icon: LayoutDashboard, 
      label: 'Dashboard', 
      path: '/dashboard',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN, UserRoles.INSTRUCTOR, UserRoles.COMPANY, UserRoles.STUDENT]
    },
    { 
      icon: Building, 
      label: 'Training Centers', 
      path: '/training-centers',
      roles: [UserRoles.ADMIN]
    },
    { 
      icon: BookOpen, 
      label: 'Courses', 
      path: '/courses',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN]
    },
    { 
      icon: Calendar, 
      label: 'Classes', 
      path: '/classes',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN, UserRoles.INSTRUCTOR]
    },
    { 
      icon: GraduationCap, 
      label: 'Instructors', 
      path: '/instructors',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN]
    },
    { 
      icon: Users, 
      label: 'Students', 
      path: '/students',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN, UserRoles.INSTRUCTOR]
    },
    { 
      icon: Building2, 
      label: 'Companies', 
      path: '/companies',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN]
    },
    { 
      icon: Building2, 
      label: 'My Company', 
      path: '/my-company',
      roles: [UserRoles.COMPANY]
    },
    { 
      icon: Award, 
      label: 'Certificates', 
      path: '/certificates',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN, UserRoles.INSTRUCTOR, UserRoles.COMPANY, UserRoles.STUDENT]
    },
    { 
      icon: Settings, 
      label: 'Admin Settings', 
      path: '/admin-settings',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN]
    },
    { 
      icon: Users, 
      label: 'Users', 
      path: '/users',
      roles: [UserRoles.ADMIN, UserRoles.TRAINING_ADMIN]
    },
    { 
      icon: Shield, 
      label: 'Security', 
      path: '/security',
      roles: [UserRoles.ADMIN]
    },
  ];

  const filteredNavItems = navItems.filter(item => item.roles.includes(user?.role));

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed left-0 top-0 h-screen w-64 bg-slate-50 border-r border-slate-200 z-50
        transform transition-transform duration-200 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        md:translate-x-0
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img 
              src="/logo.png" 
              alt="EMP Medic" 
              className="h-10 w-auto"
            />
          </Link>
          <button onClick={onClose} className="md:hidden p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            
            const handleNavClick = (e) => {
              e.preventDefault();
              onClose();
              safeNavigate(item.path);
            };
            
            return (
              <a
                key={item.path}
                href={item.path}
                onClick={handleNavClick}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg transition-colors cursor-pointer
                  ${isActive 
                    ? 'bg-white text-[#2C9DE1] shadow-sm border border-slate-200' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }
                `}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </a>
            );
          })}
        </nav>

        {/* User info at bottom */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate">
                {RoleDisplayNames[user?.role] || user?.role}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

const Header = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <header className="h-16 border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-30 flex items-center justify-between px-4 md:px-8">
      {/* Mobile menu button */}
      <button 
        onClick={onMenuClick}
        className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-5 h-5 text-slate-600" />
      </button>

      {/* Breadcrumb or page title could go here */}
      <div className="hidden md:block" />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
            <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4 text-slate-600" />
            </div>
            <span className="hidden sm:inline text-sm font-medium text-slate-700">
              {user?.first_name}
            </span>
            <ChevronDown className="w-4 h-4 text-slate-500" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium">{user?.first_name} {user?.last_name}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-menu-item">
            <Settings className="w-4 h-4 mr-2" />
            Settings
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-menu-item">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="md:pl-64">
        <Header onMenuClick={() => setSidebarOpen(true)} />
        
        <main className="p-6 md:p-8 lg:p-10">
          {children}
        </main>
      </div>
    </div>
  );
};
